import { Component, Input, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IRecentlySelectedEntity } from "@app/directives/_misc/recently-selected/recently-selected.component";
import { SortDirection } from "@app/enum/SortDirection";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IAccount } from "@app/services/account.service";
import { NavigationService } from "@app/services/navigation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { FoundationNavigationModule } from "../../foundation.navigation";
import { AccountService } from "../../_services/account.service";
import { FoundationVehiclesService } from "../../_services/vehicle.service";
import './vehicle-list.component.scss';

@UntilDestroy()
@Component({
    selector: 'foundation-vehicle-list',
    template: require("./vehicle-list.component.html")
})
export class FoundationVehicleListComponent implements OnInit {
    @Input() customerId: number;

    vehicles: IVehicle[];
    customers: IAccount[] = [];
    customerMap: Map<number, IAccount>;

    tabCount: number = 0;

    loading = false;

    tableColumns: IVTableColumn<IVehicle>[];
    tableRows: IVTableRow[];

    constructor(
        private readonly accountService: AccountService,
        private readonly navigationService: NavigationService,
        private readonly vehicleService: FoundationVehiclesService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
        this.customerId = this.navigationService.getRouteParam("customerId");
        this.createTable();
        this.loadData();
    }


    selectVehicle(row: IVTableRow): void {
        const vehicle = row.data;
        this.navigationService.go(FoundationNavigationModule.VehicleView, { vehicleId: vehicle.Id });
    }

    onRecentItemClicked(recentItem: IRecentlySelectedEntity) {
        const vehicle = this.vehicles.find(v => v.Id == recentItem.id);
        this.selectVehicle({ data: vehicle } as any);
    }

    //--

    private loadData(): void {
        this.loading = true;

        combineLatest([
            this.accountService.getCustomerAccounts(),
            !!this.customerId ? this.vehicleService.getVehiclesForAccount(this.customerId) : this.vehicleService.getVehicles()
        ]).pipe(
            untilDestroyed(this),
            tap(([customers, vehicles]) => {
                this.customers = customers;
                this.customerMap = this.accountService.createAccountMap(this.customers);

                this.vehicles = vehicles;
                this.tabCount = vehicles.length;
                this.createTableRows();

                this.loading = false;
            }))
            .subscribe();
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Created", width: 140, minTableWidth: 600, sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: vehicle => this.vTableService.createDateCell(vehicle.Created)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: vehicle => this.vTableService.createVehicleRegCell(vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationVehicles.DisplayLabel", sortable: true, minTableWidth: 800 }),
                createCellFunction: vehicle => this.vTableService.createTableCell(vehicle.ShortReg)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Company", sortable: true, minWidth: 100 }),
                createCellFunction: vehicle => this.vTableService.createTableCell(this.customerMap.get(vehicle.AccountId)?.CompanyName)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.Make", sortable: true, width: 130, minTableWidth: 600
                }),
                createCellFunction: vehicle => this.vTableService.createTableCell(vehicle.Make)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.Model", sortable: true, width: 130, minTableWidth: 600
                }),
                createCellFunction: vehicle => this.vTableService.createTableCell(vehicle.Model)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Updated", width: 140, minTableWidth: 700 }),
                createCellFunction: vehicle => this.vTableService.createDateCell(vehicle.Updated)
            }
        ]
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.vehicles, this.tableColumns);
    }
}
